/**
 * AIghtBet Frontend — Sentry instrumentation
 * ==========================================
 * Replaces the previous no-op stub with a real `@sentry/react` init.
 *
 * DSN comes from `VITE_SENTRY_DSN` at build time. When unset (local dev,
 * preview builds without secrets), every helper here becomes a no-op so
 * nothing in the call sites needs guard rails.
 *
 * Project: aightbet-frontend (org `aightbet-sports`, id 4511300399005696)
 * Companion: aightbet/observability.py for the FastAPI backend, and
 * supabase/functions/_shared/observability.ts for the 31 edge functions.
 */

import * as Sentry from '@sentry/react';

/** Whether Sentry was successfully initialized this session. */
let _initialized = false;

/**
 * Initialize the Sentry SDK. Idempotent — calling more than once is safe.
 * Returns a small interface used by the auth context (setUserContext) and
 * misc call sites (trackEvent). When SENTRY_DSN is missing the returned
 * helpers are no-ops.
 */
export function initSentry() {
  if (_initialized) return _helpers;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    console.info('[sentry] VITE_SENTRY_DSN not set — Sentry disabled');
    return _helpers;
  }

  // KAN-374: prefer the Vercel commit SHA so frontend + backend events join
  // cleanly under one `release` tag (edge functions stamp `release` from
  // GIT_COMMIT_SHA — see supabase/functions/_shared/observability.ts). Fall
  // back to the build-version timestamp from vite.config.ts so local builds
  // still get a release marker. The SHA also matches the Sentry release
  // emitted by `sentryVitePlugin` at build time, which is what makes
  // sourcemap symbolication line up.
  const sha =
    (import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA as string | undefined) ||
    undefined;
  const buildVersion =
    (typeof window !== 'undefined' && (window as any).__BUILD_VERSION__) ||
    undefined;
  const release = sha || buildVersion;

  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release,

      // Tag every event so it's filterable alongside the backend +
      // edge-function events under the same `aightbet-sports` org.
      initialScope: {
        tags: { surface: 'frontend' },
      },

      integrations: [
        Sentry.browserTracingIntegration(),
        // KAN-51: replayIntegration was the trigger for "Maximum call stack
        // size exceeded" on signup. With maskAllInputs=true, the masking
        // mutation observer mutates the DOM in response to user input on
        // the rapidly-changing signup form fields (name/email/password),
        // which fires another mutation, which the masker re-processes —
        // recursive until the stack blows. PR #45 (bdd6f535) reinforced by
        // defaulting replaysOnErrorSampleRate to 0, but the integration was
        // still mounted, so any positive env override (intentional or
        // accidental in Vercel UI) re-triggered the recursion. Removing
        // the integration entirely is the bulletproof fix. Re-introduce
        // later only with route-scoped enablement (excluded from auth).
        // KAN-38: Sentry's default breadcrumbs integration wraps `console.*`
        // to capture log breadcrumbs. In production that wrapping path
        // recurses — every code path that hits a console.log during a hot
        // surface (e.g. `register()` in SupabaseAuthContext) eventually
        // blows the stack with "Maximum call stack size exceeded", which is
        // exactly what was breaking new-user signup. Override the default
        // with `console: false`. We still get DOM, fetch, xhr, navigation,
        // and history breadcrumbs — the high-signal ones for debugging.
        Sentry.breadcrumbsIntegration({
          console: false,
          dom: true,
          fetch: true,
          xhr: true,
          history: true,
        }),
      ],

      // Conservative sampling. Tune up after we see real volume.
      tracesSampleRate: parseFloat(
        (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string) || '0.1'
      ),
      // KAN-51: replay sampling values are intentionally NOT set here.
      // The replayIntegration is no longer mounted (see integrations array
      // above), so these knobs would be no-ops. Leaving them out also
      // ensures no env-var override can accidentally re-arm the recursion
      // path. To re-enable session replay, mount the integration again with
      // route-scoped config and keep auth surfaces excluded.

      // Don't auto-include PII; we set user explicitly via setUserContext.
      sendDefaultPii: false,

      // Drop known noisy errors at the source.
      ignoreErrors: [
        // ResizeObserver loop benign warning, very common across React apps.
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications.',
        // Network hiccups we don't need to capture as Sentry issues.
        'NetworkError when attempting to fetch resource.',
        'Failed to fetch',
      ],
    });

    _initialized = true;
    console.info('[sentry] initialized (release:', release || '<none>', ')');
  } catch (e) {
    console.error('[sentry] init failed:', e);
  }

  return _helpers;
}

/**
 * Public helpers. Match the shape of the previous no-op stub so existing
 * import sites keep compiling unchanged.
 */
const _helpers = {
  setUserContext,
  trackEvent,
};

/**
 * Set the Sentry user from a Supabase user object (or clear it on logout).
 * Distinct ID = the Supabase auth user id (`sub` claim) — same as the
 * backend middleware and the edge-function wrapper, so user activity
 * joins cleanly across surfaces.
 *
 * KAN-10895 (2026-05-12): email is included so support reps can resolve
 * a crash report to a real user without a separate Supabase lookup. This
 * flips the App Privacy nutrition label Diagnostics rows from "Not linked
 * to identity" → "Linked to identity" — locked in by Will on KAN-10863.
 */
export function setUserContext(
  user: { id?: string | null; email?: string | null } | null | undefined
) {
  if (!_initialized) return;
  try {
    if (user?.id) {
      const payload: { id: string; email?: string } = { id: user.id };
      if (user.email) payload.email = user.email;
      Sentry.setUser(payload);
    } else {
      Sentry.setUser(null);
    }
  } catch {
    /* no-op */
  }
}

/**
 * Drop a breadcrumb on the current Sentry scope. Useful for capturing
 * meaningful user actions (route change, button click) so error reports
 * have context.
 */
export function trackEvent(message: string, data?: Record<string, unknown>) {
  if (!_initialized) return;
  try {
    Sentry.addBreadcrumb({
      category: 'app',
      level: 'info',
      message,
      data,
    });
  } catch {
    /* no-op */
  }
}

/**
 * Wrap a synchronous block in a manual transaction. Returns a handle
 * with `finish()` so call sites can time arbitrary work. Kept around
 * for backward-compat with the old stub's API.
 */
export const measurePerformance = (transactionName: string) => {
  if (!_initialized) {
    return {
      finish: () => {},
      setData: () => {},
      setStatus: () => {},
    };
  }
  // Sentry v10 prefers startSpan for new code; we keep the old finish/
  // setData/setStatus shape for the existing call sites that expect it.
  let _span: any = null;
  try {
    _span = Sentry.startInactiveSpan({ name: transactionName, op: 'ui.task' });
  } catch {
    /* no-op */
  }
  return {
    finish: () => {
      try {
        _span?.end();
      } catch {
        /* no-op */
      }
    },
    setData: (key: string, value: unknown) => {
      try {
        _span?.setAttribute?.(key, value as any);
      } catch {
        /* no-op */
      }
    },
    setStatus: (status: string) => {
      try {
        _span?.setStatus?.({ code: status === 'ok' ? 1 : 2, message: status });
      } catch {
        /* no-op */
      }
    },
  };
};

/**
 * Capture an exception explicitly — useful from inside React error boundaries
 * and try/catch blocks where we want a direct path to Sentry.
 */
export const captureException = (error: Error, context?: Record<string, unknown>) => {
  if (!_initialized) {
    console.error('Error captured (Sentry disabled):', error, context);
    return;
  }
  try {
    Sentry.captureException(error, context ? { extra: context } : undefined);
  } catch {
    /* no-op */
  }
};

/**
 * Drop a manually-categorized breadcrumb. Same shape as the old stub.
 */
export const addBreadcrumb = (message: string, data?: Record<string, unknown>) => {
  if (!_initialized) return;
  try {
    Sentry.addBreadcrumb({
      category: 'app',
      level: 'info',
      message,
      data,
    });
  } catch {
    /* no-op */
  }
};

export default initSentry;
